import type { ComponentKind, DesignLeaf, StructureNode } from '@kronos/domain'
import type { Tone } from '../primitives'

/**
 * The words "How it's made" uses for things the domain says in enums.
 *
 * One table per question so the diagram card, the outline row and the
 * inspector cannot drift into three phrasings of the same fact.
 */

/** How it comes to exist (§4.2) — the badge on every node. */
export const KIND: Readonly<Record<ComponentKind, { label: string; tone: Tone }>> = {
  buy: { label: 'Bought', tone: 'neutral' },
  make_external: { label: 'Vendor-made', tone: 'neutral' },
  make_internal: { label: 'Built here', tone: 'info' },
}

/**
 * Why the explosion stopped, where that is worth saying. `purchased`, `empty`
 * and `consumed` are the tree working as designed and say nothing: the kind
 * badge and the dashed edge already do. The rest are gaps a build will hit.
 */
export const GAP: Partial<Readonly<Record<DesignLeaf, { label: string; tone: Tone }>>> = {
  needs_revision: { label: 'No parts list', tone: 'warning' },
  unknown_type: { label: 'Item missing', tone: 'critical' },
  cycle: { label: 'Loops on itself', tone: 'critical' },
  too_deep: { label: 'Too deep to draw', tone: 'warning' },
}

/** A quantity can be metres, so it can be fractional — but 14.000 is noise. */
export function qty(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '')
}

/** "Short 3", "Build 1", "In stock" — the one verdict a node carries. */
export function verdict(node: StructureNode): { label: string; tone: Tone } {
  if (node.depth === 0) return { label: `Building ${qty(node.toMake)}`, tone: 'info' }
  if (node.short > 0) return { label: `Short ${qty(node.short)}`, tone: 'critical' }
  if (node.toMake > 0) return { label: `Build ${qty(node.toMake)}`, tone: node.shortBelow > 0 ? 'warning' : 'info' }
  if (node.need === 0) return { label: 'Not needed', tone: 'neutral' }
  return { label: 'In stock', tone: 'healthy' }
}

/** What a shortage means you have to do, by how the thing comes to exist. */
export function remedy(kind: ComponentKind): string {
  if (kind === 'buy') return 'buy'
  if (kind === 'make_external') return 'order from the vendor'
  return 'make'
}

/** "arm_left, arm_right" — or "joint_1 … joint_7" when the list would not fit. */
export function positionsText(positions: readonly string[]): string {
  if (positions.length <= 3) return positions.join(', ')
  return `${positions[0]} … ${positions[positions.length - 1]}`
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${qty(n)} ${n === 1 ? one : many}`
}
